import moment from 'moment';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';

import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Box from '@mui/material/Box';
import { grey } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import BeachIcon from '@mui/icons-material/BeachAccess';
import DeleteIcon from '@mui/icons-material/Delete';

import ConfirmationModal from '~/components/ConfirmationModal';
import { AbsenceResource } from '~/generated';
import Spacings from '~/modules/ui-kit/spacings';
import { useAbsences } from '~/hooks/api/useAbsences';
import { useDeleteAbsence } from '~/hooks/api/useDeleteAbsence';

import messages from './messages';
import { Link, useLocation } from '@tanstack/react-router';

export type AbsenceListProps = {
  publisherId: number;
};

const AbsenceList = (props: AbsenceListProps) => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const [absenceToDelete, setAbsenceToDelete] =
    useState<AbsenceResource | null>(null);
  const absenceQuery = useAbsences(props.publisherId);
  const mutation = useDeleteAbsence(props.publisherId, {
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.deleteFailure),
      });
      setAbsenceToDelete(null);
    },
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.deleteSuccess),
      });
      setAbsenceToDelete(null);
    },
  });
  return (
    <>
      {absenceQuery.data && absenceQuery.data.length === 0 ? (
        <Spacings.Inset scale="l">
          <Spacings.Stack scale="l" alignItems="center">
            <Box
              sx={{ borderRadius: '99999999px', backgroundColor: grey[100] }}
            >
              <Spacings.Inset scale="xl">
                <Typography component="div" color="textSecondary">
                  <BeachIcon fontSize="large" />
                </Typography>
              </Spacings.Inset>
            </Box>
            <div>
              <Typography component="p" color="textSecondary" align="center">
                <FormattedMessage {...messages.emptyStateText} />
              </Typography>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to={`/absences/create?publisherId=${
                  props.publisherId
                }&from=${encodeURIComponent(location.href)}`}
              >
                <FormattedMessage {...messages.addAbsence} />
              </Button>
            </div>
          </Spacings.Stack>
        </Spacings.Inset>
      ) : null}
      {absenceQuery.data && absenceQuery.data.length > 0 && (
        <>
          <List>
            {absenceQuery.data.map((absence) => (
              <ListItem key={absence.id}>
                <ListItemText
                  primary={intl.formatMessage(messages.absence, {
                    // TODO: Move formatting into messages with pattern EyyyyMMMdd
                    from: moment(absence.from).format('ll'),
                    to: moment(absence.to).format('ll'),
                  })}
                  secondary={
                    absence.hasConflicts
                      ? intl.formatMessage(messages.collidesWithApplications)
                      : null
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={() => setAbsenceToDelete(absence)}
                    aria-label={intl.formatMessage(messages.removeAbsence)}
                    component="span"
                    size="large"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
          <Spacings.Inset>
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to={`/absences/create?publisherId=${
                props.publisherId
              }&from=${encodeURIComponent(location.href)}`}
            >
              <FormattedMessage {...messages.addAbsence} />
            </Button>
          </Spacings.Inset>
        </>
      )}

      <ConfirmationModal
        open={Boolean(absenceToDelete)}
        title={<FormattedMessage {...messages.deleteConfirmTitle} />}
        text={
          absenceToDelete ? (
            <FormattedMessage
              {...messages.deleteConfirmText}
              values={{
                from: moment(absenceToDelete.from).format('ll'),
                to: moment(absenceToDelete.to).format('ll'),
              }}
            />
          ) : (
            ''
          )
        }
        confirmLabel={<FormattedMessage {...messages.deleteConfirmDelete} />}
        cancelLabel={<FormattedMessage {...messages.deleteConfirmCancel} />}
        onCancel={() => setAbsenceToDelete(null)}
        onConfirm={() => {
          if (!absenceToDelete) {
            return;
          }

          mutation.mutate(absenceToDelete.id!);
        }}
      />
    </>
  );
};

export default AbsenceList;
